import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';
import { formatDate } from '../../utils/formatters.js';

import { RequisitionCreateModal } from './RequisitionCreateModal.jsx';
import { RequisitionDetailModal } from './RequisitionDetailModal.jsx';

const STATUS_VARIANT = {
  DRAFT: 'neutral', SUBMITTED: 'warning', APPROVED: 'success', REJECTED: 'danger', CONVERTED: 'info',
};

export function RequisitionsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [autoMsg, setAutoMsg] = useState(null);

  const reqs = useQuery({
    queryKey: ['inventory', 'requisitions'],
    queryFn: () => inventoryApi.listRequisitions().then((r) => r.items),
  });

  const autoReorder = useMutation({
    mutationFn: () => inventoryApi.autoReorder(),
    onSuccess: (data) => {
      setAutoMsg(data.productCount > 0
        ? t('req.autoReorder.done', { products: String(data.productCount), count: String(data.requisitions.length) })
        : t('req.autoReorder.none'));
      queryClient.invalidateQueries({ queryKey: ['inventory', 'requisitions'] });
    },
    onError: (e) => setAutoMsg(e?.message || t('common.loadError')),
  });
  const rows = reqs.data || [];
  const submitted = rows.filter((r) => r.status === 'SUBMITTED').length;
  const approved = rows.filter((r) => r.status === 'APPROVED').length;

  const columns = [
    { key: 'requisitionNumber', label: t('req.col.number'), render: (r) => <span className="mono">{r.requisitionNumber}</span> },
    { key: 'neededBy', label: t('req.col.neededBy'), render: (r) => (r.neededBy ? formatDate(r.neededBy) : '-') },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'neutral'}>{t(`req.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>{t('req.action.view')}</Button> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('req.title')}</h1>
          <p className="muted">{t('req.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="secondary" onClick={() => autoReorder.mutate()} disabled={autoReorder.isPending}>
            {autoReorder.isPending ? `${t('req.action.autoReorder')}…` : t('req.action.autoReorder')}
          </Button>
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('req.action.new')}</Button>
        </div>
      </header>

      {autoMsg && <div className="alert info">{autoMsg}</div>}

      <div className="card-grid">
        <MetricCard label={t('req.metric.total')} value={String(rows.length)} />
        <MetricCard label={t('req.metric.submitted')} value={String(submitted)} trendVariant={submitted > 0 ? 'warning' : 'success'} />
        <MetricCard label={t('req.metric.approved')} value={String(approved)} />
      </div>

      <Table columns={columns} rows={rows} isLoading={reqs.isLoading} emptyMessage={t('req.empty')} />

      <RequisitionCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <RequisitionDetailModal reqId={detailId} onClose={() => setDetailId(null)} />
    </section>
  );
}
